import { ListUnitsOfMeasureForCompanyRes } from "gen/api/company/service_pb"
import { UnitOfMeasure as UnitOfMeasurePB } from "gen/company/models_pb"
import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"
import {
  Product_ProductUnitUsage,
  Product_UnitConversion,
} from "gen/product/models_pb"

import {
  ProductUnitOfMeasureConversion,
  ProductUnitOfMeasureUsage,
  ProductUnitUsageLabels,
  UnitOfMeasure,
} from "./unitsOfMeasure.types"
import { FieldMapping, mapFields } from "../../adapters"
import { Decimal } from "../../classes/decimal"
import { UUID } from "../../classes/uuid"
import { ensure } from "../../utils/nullish/nullish"

const unitOfMeasureMapping: FieldMapping<UnitOfMeasurePB, UnitOfMeasure> = {
  id: (unit) => UUID.fromPBOrThrow(unit.id, "Missing unit.id"),
  companyId: (unit) =>
    UUID.fromPBOrThrow(unit.companyId, "Missing unit.companyId"),
  createdAt: (unit) =>
    TimestampPB.toDate(ensure(unit.createdAt, "Missing unit.createdAt")),
  updatedAt: (unit) =>
    TimestampPB.toDate(ensure(unit.updatedAt, "Missing unit.updatedAt")),
  name: (unit) => unit.name,
  code: (unit) => unit.code,
  notes: (unit) => unit.notes,
  isActive: (unit) => unit.isActive,
  origin: (unit) => unit.origin,
  integrationVendor: (unit) => unit.integrationVendor,
}

export function adaptListUnitsOfMeasureForCompany(
  res: ListUnitsOfMeasureForCompanyRes
): UnitOfMeasure[] {
  const units: UnitOfMeasure[] = []
  if (!res.unitsOfMeasure) return units

  for (const unit of res.unitsOfMeasure) {
    const { data, errors } = mapFields(unit, unitOfMeasureMapping)
    if (errors) continue
    units.push(data)
  }

  return units
}

export function adaptProductUnitConversions(
  unitConversions: Product_UnitConversion[],
  companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
): ProductUnitOfMeasureConversion[] {
  return unitConversions.flatMap((conversion) => {
    if (!conversion.uomId || !conversion.conversionFactor) return []

    const unitOfMeasure = companyUnitsOfMeasureById.get(
      UUID.idString(conversion.uomId)
    )
    if (!unitOfMeasure) return []

    return {
      conversionFactor: Decimal.fromPB(conversion.conversionFactor),
      unitOfMeasure,
    }
  })
}

export function adaptProductDefaultUnitUsages(
  productDefaultUom: { [key: number]: Product_UnitConversion },
  companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
): ProductUnitOfMeasureUsage[] {
  return Object.keys(ProductUnitUsageLabels).map((usageKey) => {
    const usage = Number(usageKey) as Product_ProductUnitUsage
    const defaultConversion = productDefaultUom[usage]
    const uomId = defaultConversion?.uomId

    const unitOfMeasure = uomId
      ? companyUnitsOfMeasureById.get(UUID.idString(uomId))
      : undefined

    return { usage, unitOfMeasure }
  })
}
